<template>
  <a
    :href="sourceURL"
    :target="linkTarget"
    class="poi"
    data-test-id="poi"
    v-on="$listeners"
  >
    <div class="poi__thumbnail">
      <EThumbnail
        :src="thumbnailUrl"
        alt=""
        class="poi-thumbnail__image"
        data-test-id="poi-thumbnail-image"
      />
    </div>
    <div class="poi_details">
      <div class="poi__name">
        <span aria-hidden="true">.</span> {{ name }}
      </div>
      <ERating
        v-if="averageRating"
        class="poi_rating"
        variant="tripadvisor"
        :value="averageRating"
        :show-value="false"
        reviews-text
        :parens="false"
        :reviews="reviewCount"
      />
      <div
        v-if="formattedOpeningHours"
        class="poi_opening-hours"
        data-test-id="poi-opening-hours"
      >
        {{ formattedOpeningHours }}
      </div>
      <div class="poi__categories">
        {{ extendedSubtitle }}
      </div>
    </div>
  </a>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import ERating from '~~/common/vue/components/rating';
import EThumbnail from '~~/common/vue/components/thumbnail';
import { getPoiCategoryString, currentOpeningHours, CLOSED } from '~/results/tripadvisor';

export default {
  name: 'EPoi',
  components: {
    ERating,
    EThumbnail,
  },
  inheritAttrs: false,
  props: {
    name: {
      type: String,
      required: true,
    },
    poiType: {
      type: String,
      required: true,
    },
    subtype: {
      type: String,
      default: null,
    },
    categories: {
      type: Array,
      default: () => [],
      validator: (categories) => categories.every((cat) => typeof cat === 'string'),
    },
    averageRating: {
      type: Number,
      default: null,
    },
    image: {
      type: Object,
      default: null,
      validator: (image) => !image || image.thumbnailUrl,
    },
    openingHoursWeek: {
      type: Object,
      default: () => {},
    },
    reviewCount: {
      type: Number,
      default: 0,
    },
    sourceURL: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState('global', ['locale']),
    ...mapGetters('user', ['linkTarget']),
    extendedSubtitle() {
      return getPoiCategoryString(
        null,
        this.categories ?? [],
      );
    },
    thumbnailUrl() {
      return this.image?.thumbnailUrl ?? '';
    },
    formattedOpeningHours() {
      const formattedHours = currentOpeningHours(this.openingHoursWeek, this.locale);

      if (formattedHours === CLOSED) {
        return this.$t('web.entities.tripadvisor.closedtoday');
      }

      return formattedHours;
    },
  },
};
</script>

<style lang="scss" scoped>
$thumbail-size: 72px;

.poi {
  padding: $space-1s;
  display: flex;
  border-radius: $space-2s;

  &:hover {
    background: var(--color-button-background-transparent-hover);
  }
}

.poi-thumbnail__image {
  height: $thumbail-size;
  width: $thumbail-size;
  border-radius: $space-2s;
  overflow: hidden;
  // border only visible when image is missing
  box-shadow: 0 0 0 1px var(--color-decorative-border-1) inset;
  background: var(--color-background-secondary);
  margin-right: $space-m;
}

.poi__name,
.poi__categories {
  @include line-clamp(1);
}

.poi__name {
  counter-increment: poi-index;
  color: var(--color-link-results-default);

  .poi:hover & {
    text-decoration: underline;
  }

  &::before {
    content: counter(poi-index);
  }
}

.poi__categories {
  color: var(--color-text-secondary);
}

.poi_rating {
  font-size: $font-s;
}
</style>
